import * as React from 'react';
import {
  Typography, Box, Button, Grid, Collapse, Skeleton,
  IconButton, InputAdornment, useMediaQuery, useTheme,
} from '@mui/material';
import { connect } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Table, Empty } from 'antd';
import { Search } from '@mui/icons-material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { centered_flex_box, main_button, MainInput } from '../app/components/Styles';
import { getBazaars, getBazaarSummary } from '../app/store/actions/dataActions';
import { cardShell, StatusPill, formatInt, formatEgp, pageTitle } from '../app/glass/styles';
import SearchFilterBar, { FilterChips, plusIcon } from '../app/glass/SearchFilterBar';

const STATUS_CHIPS = [
  ['', 'bazaars.allStatuses'],
  ['Pending', 'bazaars.statusLabels.Pending'],
  ['Accepted', 'bazaars.statusLabels.Accepted'],
  ['Fulfilled', 'bazaars.statusLabels.Fulfilled'],
  ['InProgress', 'bazaars.statusLabels.InProgress'],
  ['Completed', 'bazaars.statusLabels.Completed'],
  ['Cancelled', 'bazaars.statusLabels.Cancelled'],
  ['Rejected', 'bazaars.statusLabels.Rejected'],
];

export const BazaarsList = ({ getBazaars, getBazaarSummary, bazaars, bazaarSummary, isLoading }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams, setSearchParams] = useSearchParams();

  const [page, setPage] = React.useState(parseInt(searchParams.get('page'), 10) || 1);
  const [pageSize, setPageSize] = React.useState(parseInt(searchParams.get('pageSize'), 10) || 10);
  const [status, setStatus] = React.useState(searchParams.get('status') || '');
  const [search, setSearch] = React.useState(searchParams.get('search') || '');
  const [showFilters, setShowFilters] = React.useState(false);

  const statusLabel = React.useCallback(
    (value) => t(`bazaars.statusLabels.${value}`, { defaultValue: value }),
    [t]
  );

  const modeLabel = (mode) => (mode === 'POS' ? t('bazaars.modePOS') : t('bazaars.modeOutbound'));

  const syncParams = (next = {}) => {
    const p = next.page ?? page;
    const ps = next.pageSize ?? pageSize;
    const st = next.status !== undefined ? next.status : status;
    const q = next.search !== undefined ? next.search : search;
    const params = { page: String(p), pageSize: String(ps) };
    if (st) params.status = st;
    if (q) params.search = q;
    setSearchParams(params, { replace: true });
  };

  const fetchList = React.useCallback((overrides = {}) => {
    const p = overrides.page ?? page;
    const ps = overrides.pageSize ?? pageSize;
    getBazaars({
      page: p,
      limit: ps,
      status: (overrides.status ?? status) || undefined,
      search: (overrides.search ?? search) || undefined,
    });
  }, [getBazaars, page, pageSize, status, search]);

  React.useEffect(() => {
    getBazaarSummary({});
  }, [getBazaarSummary]);

  React.useEffect(() => {
    fetchList();
  }, [page, pageSize, status]);

  const handleSearch = () => {
    setPage(1);
    syncParams({ page: 1, search });
    fetchList({ page: 1, search });
  };

  const handleStatusChange = (value) => {
    setPage(1);
    setStatus(value);
    syncParams({ page: 1, status: value });
  };

  const clearFilters = () => {
    setStatus('');
    setSearch('');
    setPage(1);
    setSearchParams({ page: '1', pageSize: String(pageSize) }, { replace: true });
    getBazaars({ page: 1, limit: pageSize });
  };

  const handleTableChange = (pagination) => {
    setPage(pagination.current);
    setPageSize(pagination.pageSize);
    syncParams({ page: pagination.current, pageSize: pagination.pageSize });
  };

  const columns = [
    { title: t('bazaars.name'), dataIndex: 'name' },
    { title: t('bazaars.location'), dataIndex: 'location' },
    {
      title: t('bazaars.dates'),
      render: (_, row) => `${moment(row.startDate).format('DD/MM/YY')} - ${moment(row.endDate).format('DD/MM/YY')}`,
    },
    {
      title: t('bazaars.mode'),
      render: (_, row) => (
        <span style={{ fontSize: 12.5, color: 'var(--tx2)' }}>{modeLabel(row.fulfillmentMode)}</span>
      ),
    },
    {
      title: t('bazaars.status'),
      render: (_, row) => {
        const display = row.displayStatus || row.status;
        return <StatusPill status={display}>{statusLabel(display)}</StatusPill>;
      },
    },
    {
      title: t('common.actions'),
      render: (_, row) => (
        <button type="button" className="cz-chip" onClick={() => navigate(`/bazaars/${row._id}`)}>
          {t('common.view')}
        </button>
      ),
    },
  ];

  const rows = bazaars?.docs || [];

  const kpiItems = [
    {
      label: t('bazaars.dashboardTotal'),
      value: formatInt(bazaarSummary?.totalBazaars),
      sub: t('bazaars.title'),
    },
    {
      label: t('bazaars.dashboardActive'),
      value: formatInt(bazaarSummary?.inProgressCount),
      sub: statusLabel('InProgress'),
    },
    {
      label: t('bazaars.dashboardPending'),
      value: formatInt(bazaarSummary?.pendingBazaars),
      sub: statusLabel('Pending'),
    },
    {
      label: t('bazaars.dashboardRevenue'),
      value: `${formatEgp(bazaarSummary?.totalRevenue || 0)} EGP`,
      sub: t('bazaars.payment'),
    },
    {
      label: t('bazaars.dashboardUnits'),
      value: formatInt(bazaarSummary?.unitsSold),
      sub: t('bazaars.unitsSold'),
    },
  ];

  return (
    <div>
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={pageTitle}>{t('bazaars.title')}</div>
        </div>

        <div
          className="cz-g2"
          style={{
            background: 'var(--card-solid)',
            borderRadius: 26,
            padding: isMobile ? '18px 20px' : '24px 34px',
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(5, 1fr)',
            gap: 24,
            alignItems: 'center',
            border: '1px solid var(--s12)',
            boxShadow: 'inset 0 1px 0 var(--s14), 0 22px 50px var(--sh45)',
          }}
        >
          {kpiItems.map((st) => (
            <div key={st.label}>
              <div style={{ fontSize: 12, color: 'var(--mut2)', marginBottom: 10 }}>{st.label}</div>
              <div style={{ fontSize: isMobile ? 20 : 26, fontWeight: 500, color: 'var(--tx)' }}>
                {isLoading && bazaarSummary == null ? <Skeleton width={48} /> : st.value}
              </div>
              <div style={{ fontSize: 11, color: 'var(--mut2)', marginTop: 6 }}>{st.sub}</div>
            </div>
          ))}
        </div>

        <FilterChips
          value={status}
          onChange={handleStatusChange}
          options={STATUS_CHIPS.map(([value, labelKey]) => [value, t(labelKey, { defaultValue: value || 'All' })])}
        />

        <SearchFilterBar
          placeholder={t('bazaars.searchPlaceholder')}
          value={search}
          onChange={setSearch}
          onSearch={handleSearch}
          isMobile={isMobile}
          filterOpen={showFilters}
          onToggleFilter={() => setShowFilters(!showFilters)}
          filterLabel={t('common.filter')}
          actions={(
            <>
              <button type="button" className="cz-chip" onClick={() => fetchList()} disabled={isLoading}>
                {t('common.refresh')}
              </button>
              <button type="button" className="cz-chip red" onClick={() => navigate('/bazaars/create')}>
                {t('bazaars.create')}
                {plusIcon}
              </button>
            </>
          )}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sx={{ ...centered_flex_box }}>
                <Button sx={{ ...main_button }} fullWidth onClick={clearFilters}>
                  {t('common.clear')}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </SearchFilterBar>

        <div style={cardShell}>
          <Box
            sx={{
              width: '100%',
              overflowX: 'auto',
              '& .ant-table-wrapper': { width: '100%' },
              '& .ant-table': { minWidth: isMobile ? 300 : 900 },
            }}
          >
            <Table
              loading={isLoading}
              columns={columns}
              locale={{ emptyText: <Empty description={t('bazaars.empty')} /> }}
              dataSource={rows.map((r) => ({ ...r, key: r._id }))}
              onChange={handleTableChange}
              pagination={{
                current: page,
                pageSize,
                total: bazaars?.totalDocs || 0,
                showSizeChanger: true,
              }}
              scroll={{ x: isMobile ? 800 : undefined }}
            />
          </Box>
        </div>
      </Box>
    </div>
  );
};

const mapStateToProps = (state) => ({
  bazaars: state.records.bazaars,
  bazaarSummary: state.records.bazaarSummary,
  isLoading: state.records.isLoading,
});

export default connect(mapStateToProps, { getBazaars, getBazaarSummary })(BazaarsList);
